/**
 * Budget Modal Template Component
 *
 * Reusable modal template that encapsulates common modal patterns for the budgeting module.
 * Provides built-in functionality for:
 * - Exit confirmation logic based on unsaved changes
 * - Local state management (changes kept in modal, not Redux)
 * - Save/Cancel button patterns with loading states
 * - Flexible sizing and styling
 * - Form data synchronization when modal opens
 *
 * USAGE EXAMPLES:
 *
 * Example 1: Simple form modal with change detection
 * ```tsx
 * <BudgetModalTemplate
 *   open={open}
 *   onClose={onClose}
 *   title="Edit Study Data"
 *   icon="edit"
 *   width="700px"
 *   hasChanges={formData !== originalData}
 *   onSave={handleSave}
 *   isSaving={isSaving}
 * >
 *   <Form>
 *     <Input value={formData.name} onChange={...} />
 *   </Form>
 * </BudgetModalTemplate>
 * ```
 *
 * Example 2: Modal with custom footer buttons
 * ```tsx
 * <BudgetModalTemplate
 *   open={open}
 *   onClose={onClose}
 *   title="Calculation Preview"
 *   width="large"
 *   hasChanges={false}  // Always show confirmation
 *   customFooter={
 *     <>
 *       <Button onClick={onClose}>Cancel</Button>
 *       <Button primary onClick={onApply}>Apply Calculations</Button>
 *     </>
 *   }
 * >
 *   <CalculationResults data={previewData} />
 * </BudgetModalTemplate>
 * ```
 *
 * Example 3: Modal with conditional button labels
 * ```tsx
 * <BudgetModalTemplate
 *   open={open}
 *   onClose={onClose}
 *   title="Edit Positions"
 *   width="900px"
 *   hasChanges={pendingChanges.size > 0}
 *   onSave={handleSave}
 *   isSaving={isSaving}
 *   saveLabel="Save All Changes"
 *   cancelLabel="Discard"
 * >
 *   <PositionsList onUpdate={handleUpdate} />
 * </BudgetModalTemplate>
 * ```
 *
 * @component
 */

import React from 'react';
import { Icon, SemanticICONS } from 'semantic-ui-react';
import WizardComponent from '../../../components/Modal/WizardComponent';
import ModalFooter from './ModalFooter';

export interface BudgetModalTemplateProps {
  /** Controls modal visibility */
  open: boolean;

  /** Called when modal is closed (Cancel or X button) */
  onClose: () => void;

  /** Modal title text (or a node, e.g. an icon + text) */
  title: React.ReactNode;

  /** Optional icon to display next to title */
  icon?: SemanticICONS;

  /** Modal content (form fields, tables, etc.) */
  children: React.ReactNode;

  /** Whether there are unsaved changes (controls exit confirmation) */
  hasChanges: boolean;

  /** Called when Save button is clicked (if not using customFooter) */
  onSave?: () => void;

  /** Whether save operation is in progress */
  isSaving?: boolean;

  /** Custom footer content (replaces default Save/Cancel buttons) */
  customFooter?: React.ReactNode;

  /** Label for cancel button (default: "Cancel") */
  cancelLabel?: string;

  /** Label for save button (default: "Save") */
  saveLabel?: string;

  /** Label shown during save operation (default: "Saving...") */
  savingLabel?: string;

  /** Modal width (default: "700px", or use "large", "small") */
  width?: string;

  /** Modal minimum width (optional) */
  minWidth?: string;

  /** Modal maximum width (optional) */
  maxWidth?: string;

  /** Extra class on the .ui.modal element, for scoping modal-specific CSS */
  modalClassName?: string;

  /** Content padding (default: "2rem") */
  contentPadding?: string;

  /** Minimum height (default: "13rem") */
  minHeight?: string;

  /** Maximum height (optional) */
  maxHeight?: string;

  /** Border style for header (optional) */
  borderHeader?: string;

  /** Border style for footer (optional) */
  borderTopFooter?: string;

  /** Header font size (optional) */
  headerFontSize?: string;

  /** Footer padding (optional, defaults to contentPadding) */
  footerPadding?: string;

  /** Additional content to render in footer (error messages, etc.) */
  additionalFooterContent?: React.ReactNode;

  /** Disable save button (in addition to hasChanges check) */
  disableSave?: boolean;

  /** Content overflow-x style (optional, default: "visible") */
  contentOverflowX?: string;

  /** Content overflow-y style (optional, default: "visible") */
  contentOverflowY?: string;
}

/**
 * Reusable modal template for budgeting module
 * Handles common patterns: exit confirmation, local state, save/cancel logic
 */
const BudgetModalTemplate: React.FC<BudgetModalTemplateProps> = ({
  open,
  onClose,
  title,
  icon,
  children,
  hasChanges,
  onSave,
  isSaving = false,
  customFooter,
  cancelLabel = 'Cancel',
  saveLabel = 'Save',
  savingLabel = 'Saving...',
  width = '700px',
  minWidth,
  maxWidth,
  contentPadding = '2rem',
  minHeight = '13rem',
  maxHeight,
  borderHeader,
  borderTopFooter,
  headerFontSize,
  footerPadding,
  additionalFooterContent,
  disableSave = false,
  contentOverflowX,
  contentOverflowY,
  modalClassName
}) => {
  /**
   * Renders modal header with optional icon
   */
  const renderHeader = () => (
    <div>
      {icon && <Icon name={icon} />}
      {title}
    </div>
  );

  /**
   * Renders footer with Save/Cancel buttons or custom footer
   */
  const renderFooter = () => {
    if (customFooter) {
      return customFooter;
    }

    if (!onSave) {
      console.warn('BudgetModalTemplate: onSave prop required when not using customFooter');
      return null;
    }

    return (
      <ModalFooter
        onCancel={onClose}
        onPrimaryAction={onSave}
        cancelLabel={cancelLabel}
        primaryLabel={saveLabel}
        primaryDisabled={disableSave}
        isLoading={isSaving}
        loadingLabel={savingLabel}
        additionalContent={additionalFooterContent}
      />
    );
  };

  const footer = renderFooter();

  return (
    <WizardComponent
      open={open}
      close={onClose}
      content={<>{children}</>}
      footer={footer ? <>{footer}</> : undefined}
      headerComponent={renderHeader()}
      width={width}
      minWidth={minWidth}
      maxWidth={maxWidth}
      contentPadding={contentPadding}
      minHeight={minHeight}
      maxHeight={maxHeight}
      borderHeader={borderHeader}
      borderTopFooter={borderTopFooter}
      headerContentFontSize={headerFontSize}
      hidePopup={!hasChanges}
      contentOverflowX={contentOverflowX}
      contentOverflowY={contentOverflowY}
      /* Marks every budget dialog for the footer rules in modal.overrides. */
      modalClassName={['budget-modal', modalClassName].filter(Boolean).join(' ')}
      actionPadding={footerPadding ?? '20px'}
      footerFitContent
    />
  );
};

export default BudgetModalTemplate;
